import React, { useState } from 'react'
import { motion, useScroll, useTransform } from 'framer-motion'
import { Chrome, X, Menu } from 'lucide-react'
import { LazyLoadImage } from 'react-lazy-load-image-component'
import 'react-lazy-load-image-component/src/effects/blur.css'
import chronoLogo from '../images/Chrono Session - removed bg logo.png'

interface NavigationProps {
  activeSection: string
}

const Navigation: React.FC<NavigationProps> = ({ activeSection }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const { scrollY } = useScroll()
  
  const navBackground = useTransform(scrollY, [0, 80], ['rgba(255,255,255,0)', 'rgba(255,255,255,0.98)'])
  const navBorder = useTransform(scrollY, [0, 80], ['rgba(0,0,0,0)', 'rgba(0,0,0,0.1)'])

  // Updated navigation - Features, Pricing, FAQ, Contact
  const navItems = [
    { id: 'features', label: 'Features', type: 'scroll' },
    { id: 'pricing', label: 'Pricing', type: 'scroll' },
    { id: 'faq', label: 'FAQ', type: 'scroll' },
    { id: 'contact', label: 'Contact', type: 'link', href: '/contact' }
  ]

  return (
    <motion.nav 
      className="fixed top-4 sm:top-6 left-1/2 transform -translate-x-1/2 z-[100] transition-all duration-500 w-[85%] sm:w-[80%] md:w-[70%] lg:w-[60%] xl:w-[50%] max-w-sm sm:max-w-none"
    >
      <motion.div
        className="bg-white/90 backdrop-blur-xl rounded-full border shadow-lg px-3 sm:px-4 md:px-6 py-2.5 sm:py-3 w-full"
        style={{ 
          backgroundColor: navBackground,
          borderColor: navBorder
        }}
      >
        <div className="flex items-center justify-between">
          {/* Logo */}
          <motion.div 
            className="flex items-center space-x-2 sm:space-x-3"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            <div className="flex items-center justify-center">
              <LazyLoadImage 
                src={chronoLogo} 
                alt="Chrono Session" 
                className="h-5 w-auto sm:h-6 md:h-8 object-contain" 
                width="20" 
                height="20"
                effect="blur"
                placeholderSrc={chronoLogo}
              />
            </div>
          </motion.div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-6 lg:space-x-8 mx-4 lg:mx-8">
            {navItems.map((item, index) => (
              item.type === 'link' ? (
                <motion.a
                  key={item.id}
                  href={item.href}
                  className="relative text-sm font-medium transition-all duration-300 px-3 py-2 rounded-full text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  {item.label}
                </motion.a>
              ) : (
                <motion.a
                  key={item.id}
                  href={`#${item.id}`}
                  onClick={(e) => {
                    e.preventDefault()
                    const element = document.getElementById(item.id)
                    if (element) {
                      element.scrollIntoView({ 
                        behavior: 'smooth',
                        block: 'start'
                      })
                    }
                  }}
                  className={`relative text-sm font-medium transition-all duration-300 px-3 py-2 rounded-full ${
                    activeSection === item.id 
                      ? 'text-blue-600 bg-blue-50' 
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  {item.label}
                </motion.a>
              )
            ))}
          </div>
          
          {/* CTA Button */}
          <div className="flex items-center space-x-2 sm:space-x-4">
            <motion.a
              href="https://chrome.google.com/webstore/detail/mmoeppckalbggegohbocmbkpfmnocpcd"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-3 sm:px-3 md:px-4 py-2 sm:py-2 bg-black text-white rounded-full font-medium text-xs sm:text-sm hover:bg-gray-800 transition-all duration-300"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <Chrome className="w-3 h-3 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" />
              <span className="hidden md:inline">Start Free</span>
              <span className="md:hidden">Start Free</span>
            </motion.a>
            
            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-1.5 sm:p-2 rounded-full hover:bg-gray-100 transition-all duration-300"
            >
              {isMenuOpen ? <X className="w-4 h-4 sm:w-5 sm:h-5" /> : <Menu className="w-4 h-4 sm:w-5 sm:h-5" />}
            </button>
          </div>
        </div>
      </motion.div>

      {/* Mobile Menu - Perfectly centered positioning */}
      {isMenuOpen && (
        <motion.div
          initial={{ opacity: 0, y: -20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -20, scale: 0.95 }}
          transition={{ duration: 0.2 }}
          className="md:hidden absolute top-[4rem] sm:top-[4.5rem] left-0 right-0 mx-auto bg-white/95 backdrop-blur-xl rounded-2xl border shadow-lg p-4 w-[95%] max-w-xs z-[110]"
        >
          <div className="space-y-2">
            {navItems.map((item, index) => (
              item.type === 'link' ? (
                <motion.a
                  key={item.id}
                  href={item.href}
                  className="block text-gray-700 hover:text-blue-600 font-medium transition-colors duration-300 px-3 py-2.5 rounded-lg hover:bg-gray-50 text-sm"
                  onClick={() => setIsMenuOpen(false)}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                >
                  {item.label}
                </motion.a>
              ) : (
                <motion.a
                  key={item.id}
                  href={`#${item.id}`}
                  className={`block text-gray-700 hover:text-blue-600 font-medium transition-colors duration-300 px-3 py-2.5 rounded-lg hover:bg-gray-50 text-sm ${
                    activeSection === item.id ? 'text-blue-600 bg-blue-50' : ''
                  }`}
                  onClick={(e) => {
                    e.preventDefault()
                    setIsMenuOpen(false)
                    const element = document.getElementById(item.id)
                    if (element) {
                      element.scrollIntoView({ 
                        behavior: 'smooth',
                        block: 'start'
                      })
                    }
                  }}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                >
                  {item.label}
                </motion.a>
              )
            ))}
          </div>
        </motion.div>
      )}
    </motion.nav>
  )
}

export default Navigation 